import { useState } from 'react';
import { content } from '../../content';
import type { GameState } from '../../core/types';
import type { CommandHandler } from '../App';
import { CardView } from '../components/CardView';
import { GameHeader } from '../components/GameHeader';

export function RewardScreen({ state, onCommand }: { state: GameState; onCommand: CommandHandler }) {
  const reward = state.reward;
  const [selectedCardId, setSelectedCardId] = useState<string | undefined>();
  const [selectedRelicId, setSelectedRelicId] = useState<string | undefined>();
  if (!reward) return null;
  const requiresRelic = reward.relicIds.length > 0;
  const canTakeCard = reward.cardIds.length > 0;
  const rewardCopy = requiresRelic
    ? canTakeCard
      ? 'Choose one recovered relic. You may also add one technique to your deck.'
      : 'Choose one recovered relic before continuing.'
    : 'Choose one technique for your deck, or leave the cards behind.';

  return (
    <div className="screen reward-screen">
      <GameHeader state={state} eyebrow="Salvage" />
      <main id="main-content" className="reward-layout">
        <section className="reward-heading" aria-labelledby="reward-title">
          <span className="reward-seal" aria-hidden="true"><i /><i /></span>
          <p className="kicker">Passage secured</p>
          <h1 id="reward-title">Choose what survives</h1>
          <p>
            Recovered supplies yield <strong>{reward.iron} Iron</strong>. {rewardCopy}
          </p>
        </section>
        <section className="reward-choices" aria-label="Expedition rewards">
          {reward.relicIds.map((relicId) => {
            const relic = content.relics[relicId];
            return relic ? (
              <button
                type="button"
                className={`relic-choice${selectedRelicId === relic.id ? ' is-selected' : ''}`}
                key={relic.id}
                aria-label={`${relic.name}, ${relic.rarity} relic. ${relic.description}`}
                aria-pressed={selectedRelicId === relic.id}
                onClick={() => setSelectedRelicId(relic.id)}
              >
                <img className="relic-art" src={`/art/cathedris/relics/${relic.id}.webp`} alt="" />
                <span className="relic-choice__rarity">{relic.rarity} relic</span>
                <strong>{relic.name}</strong>
                <span>{relic.description}</span>
              </button>
            ) : null;
          })}
          {reward.cardIds.map((cardId) => {
            const definition = content.cards[cardId];
            return definition ? (
              <CardView
                key={cardId}
                definition={definition}
                choice
                selected={selectedCardId === cardId}
                onClick={() =>
                  setSelectedCardId((current) => (current === cardId ? undefined : cardId))
                }
              />
            ) : null;
          })}
        </section>
        <div className="reward-footer">
          <span><strong>+{reward.iron}</strong> Iron is collected when you leave.</span>
          <span className="reward-footer__actions">
            {canTakeCard && !selectedCardId ? <small>Leaving without a card</small> : null}
            <button
              className="primary-action"
              type="button"
              disabled={requiresRelic && !selectedRelicId}
              onClick={() =>
                onCommand({
                  type: 'choose_reward',
                  cardDefinitionId: selectedCardId,
                  relicId: selectedRelicId,
                })
              }
            >
              {requiresRelic && !selectedCardId ? 'Take relic · skip card' : 'Take selected salvage'}
            </button>
          </span>
        </div>
      </main>
    </div>
  );
}
